import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import { Button } from './Button';
import { Input } from './Input';

export interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  pageSize?: number;
  totalItems?: number;
  pageLabel?: string;
  itemsLabel?: string;
  ofLabel?: string;
}

const clampPage = (page: number, totalPages: number) =>
  Math.min(Math.max(1, page), Math.max(1, totalPages));

export const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  onPageChange,
  pageSize,
  totalItems,
  pageLabel,
  itemsLabel,
  ofLabel = 'of',
}) => {
  // TanStack getPageCount() is 0 for an empty table and -1 when the server
  // page count is unknown. Treating either value as one page snaps a later
  // pageIndex back to the first page. Item totals still bound Next and a typed
  // page without rewriting that pageIndex.
  const pageCountKnown = totalPages >= 1;
  const lastPage = pageCountKnown ? totalPages : 1;
  const safePage = pageCountKnown ? clampPage(currentPage, lastPage) : Math.max(1, currentPage);
  const itemLastPage =
    pageSize != null && pageSize > 0 && totalItems != null && totalItems > 0
      ? Math.max(1, Math.ceil(totalItems / pageSize))
      : undefined;
  const navLastPage = pageCountKnown ? lastPage : itemLastPage;
  const clampNav = (page: number) =>
    navLastPage != null ? clampPage(page, navLastPage) : Math.max(1, page);
  const goTo = (page: number) => onPageChange(clampNav(page));
  const atStart = safePage <= 1;
  const atEnd = navLastPage != null ? safePage >= navLastPage : totalPages === 0;

  const navRef = useRef<HTMLElement>(null);
  const pressStartedOnStepButton = useRef(false);
  const [draft, setDraft] = useState(String(currentPage));
  useEffect(() => {
    setDraft(String(safePage));
  }, [safePage]);

  useEffect(() => {
    if (!pageCountKnown) return;
    if (currentPage !== safePage) onPageChange(safePage);
  }, [pageCountKnown, currentPage, safePage, onPageChange]);

  // Commit only on Enter/blur: navigating per keystroke would load the page for
  // every intermediate digit, and the clamped result syncing back into `draft`
  // would overwrite what is still being typed.
  const commitDraft = () => {
    const parsed = Number.parseInt(draft, 10);
    if (!Number.isFinite(parsed)) {
      setDraft(String(safePage));
      return;
    }
    const nextPage = clampNav(parsed);
    setDraft(String(safePage));
    if (nextPage !== safePage) onPageChange(nextPage);
  };

  const isStepButton = (target: EventTarget | null) =>
    target instanceof Element &&
    navRef.current?.contains(target) === true &&
    target.closest('button') !== null;

  // A step button click must move exactly one step, so it drops the draft
  // instead of committing it. Safari and Firefox on macOS do not focus a
  // <button> on click, so that blur carries no `relatedTarget` and the press
  // target is the only evidence left. Tab onto a step button has no press, so
  // that blur still commits. Presses elsewhere in the nav (the range label,
  // empty space, the input) must still commit.
  const handlePageInputBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    const next = event.relatedTarget;
    const skipForStepPress =
      pressStartedOnStepButton.current && (next == null || isStepButton(next));
    pressStartedOnStepButton.current = false;
    if (skipForStepPress) return;
    commitDraft();
  };

  const hasRange = pageSize != null && totalItems != null;
  const rangePage =
    hasRange && itemLastPage != null && totalItems > 0
      ? Math.min(safePage, itemLastPage)
      : safePage;
  const rangeStart = !hasRange || totalItems === 0 ? 0 : (rangePage - 1) * pageSize + 1;
  const rangeEnd = !hasRange || totalItems === 0 ? 0 : Math.min(rangePage * pageSize, totalItems);
  const rangeLabel = hasRange
    ? [`${rangeStart} - ${rangeEnd}`, itemsLabel, ofLabel, totalItems]
        .filter((part) => part !== undefined && part !== '')
        .join(' ')
    : undefined;

  return (
    <nav
      ref={navRef}
      aria-label="pagination"
      onMouseDownCapture={(event) => {
        pressStartedOnStepButton.current = isStepButton(event.target);
      }}
      className="tw flex w-full items-center justify-end gap-1"
    >
      {pageLabel ? (
        <span className="tw whitespace-nowrap text-foreground-secondary body-sm">{pageLabel}</span>
      ) : null}
      <Input
        type="text"
        inputMode="numeric"
        aria-label="Page number"
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
        // Clears a flag left by an earlier step-button press when focus returns
        // by keyboard, which sends no mousedown into the nav.
        onFocus={() => {
          pressStartedOnStepButton.current = false;
        }}
        onBlur={handlePageInputBlur}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            commitDraft();
          } else if (event.key === 'Escape') {
            setDraft(String(safePage));
          }
        }}
        className="tw h-7 w-12 px-1 text-center"
      />
      {pageCountKnown ? (
        <span className="tw whitespace-nowrap pr-1 text-foreground-secondary body-sm">
          {ofLabel} {lastPage}
        </span>
      ) : null}
      <Button
        type="button"
        variant="ghost"
        size="icon-sm"
        aria-label="First page"
        disabled={atStart}
        onClick={() => goTo(1)}
      >
        <ChevronsLeft className="size-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="icon-sm"
        aria-label="Previous page"
        disabled={atStart}
        onClick={() => goTo(safePage - 1)}
      >
        <ChevronLeft className="size-4" />
      </Button>
      {rangeLabel ? (
        <span className="tw whitespace-nowrap px-2 text-foreground-secondary body-sm">
          {rangeLabel}
        </span>
      ) : null}
      <Button
        type="button"
        variant="ghost"
        size="icon-sm"
        aria-label="Next page"
        disabled={atEnd}
        onClick={() => goTo(safePage + 1)}
      >
        <ChevronRight className="size-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="icon-sm"
        aria-label="Last page"
        disabled={atEnd || !pageCountKnown}
        onClick={() => goTo(lastPage)}
      >
        <ChevronsRight className="size-4" />
      </Button>
    </nav>
  );
};
